import { Box } from '@chakra-ui/react';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { RThirdTitle } from '@resistapp/client/components/headers/r-third-title';
import { MultiOptionConfirmModal } from '@resistapp/client/components/modals/multi-option-confirmation-modal';
import { Grid, GridCell } from '@resistapp/client/components/shared/grid';
import { GridView } from '@resistapp/client/components/shared/layout';
import { theme } from '@resistapp/client/components/shared/theme';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { fetchProjectCts } from '@resistapp/client/hooks/api';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { useIsModifyAsAdminActive } from '@resistapp/client/hooks/use-research-admin';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { compileCtsFiles } from '@resistapp/common/compile-cts-files';
import { getHeatmapCsvExportContent, getNumberOfDetectedGenesByBioNumCsv } from '@resistapp/common/export';
import { hasOverviewForProject, isNonDemo, RESEARCH_ACCESS_EXCEPTION_PROJECT_ID } from '@resistapp/common/features';
import { NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { AssayInfo } from '@resistapp/common/assays';
import { FullProject, FullSamplesByUID, LabWorkbookLink, NormalisationMode, Project } from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import { chunk, Dictionary, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLinkIcon } from '../../components/icons/external-link-icon';
import { DownloadLink } from '../../components/links/download-link';
import { UrlLink } from '../../components/links/url-link';
import {
  canDownloadAyResearchResults,
  flattenProjectCtsRows,
  getCtsFilenames,
  getDetectedGenesDownloadNormalisationMode,
  getMachineLabelByCtsFilename,
  getProjectDownloadableNormalisationModes,
  shouldHideResearchOnlyDemoExtras,
} from './download-and-link-section-utils';

const PROJECT_REFERENCE_LINKS: Record<number, Array<{ href: string; text: string }>> = {
  [RESEARCH_ACCESS_EXCEPTION_PROJECT_ID]: [
    {
      href: 'https://doi.org/10.1016/j.jhin.2021.09.008',
      text: 'Routine wastewater-based monitoring of antibiotic resistance in two Finnish hospitals: focus on carbapenem resistance genes and genes associated with bacteria causing hospital-acquired infections',
    },
  ],
};

interface Props {
  project: FullProject;
  displayMode?: 'full' | 'ct-only';
}

type CtsQaColumnMode = 'include' | 'skip';

export function DownloadAndLinkSection({ project, displayMode = 'full' }: Props) {
  const { user } = useUser();
  const [isModifyAsAdminActive] = useIsModifyAsAdminActive();
  const isCtOnly = displayMode === 'ct-only';

  const normalisationMode = useNormalisationMode();
  const { getAssayInfo, getGroup } = useAssayContext();
  const plotFileNames = getExtraFilenames(project);
  const showLegacyProjectFiles = isModifyAsAdminActive && isResistomapAdminUser(user) && plotFileNames.length > 0;
  const isResistomapAdmin = isResistomapAdminUser(user);
  const hideResearchOnlyDemoExtras = shouldHideResearchOnlyDemoExtras(user, project.id);
  const canDownloadAy = canDownloadAyResearchResults(user, project.id);
  const downloadableNormalisationModes = useMemo(
    () => getProjectDownloadableNormalisationModes(project.samplesByUID, canDownloadAy),
    [project.samplesByUID, canDownloadAy],
  );
  const detectedGenesDownloadNormalisationMode = useMemo(
    () => getDetectedGenesDownloadNormalisationMode(normalisationMode, downloadableNormalisationModes),
    [normalisationMode, downloadableNormalisationModes],
  );
  const showAnalysisResultsSection = !isCtOnly && (downloadableNormalisationModes.length > 0 || showLegacyProjectFiles);
  // The raw Cq rows are large, so they are loaded from the API and compiled into the downloadable files only when a
  // file is downloaded. The compiled files are cached per project.
  const { chips: projectChips, id: projectId } = project;
  const queryClient = useQueryClient();
  const ctsFilenames = useMemo(() => getCtsFilenames(projectChips), [projectChips]);
  const machineLabelByFilename = useMemo(() => getMachineLabelByCtsFilename(project.chips), [project.chips]);
  const workbookLinks = project.linkedLabWorkbooks ?? [];
  const projectReferences = PROJECT_REFERENCE_LINKS[project.id] ?? [];
  const hasCtFiles = ctsFilenames.length > 0;
  const compiledCtsFilesRef = useRef<{ plain?: Dictionary<string>; withQaRuleInfo?: Dictionary<string> }>({});
  const [qaModalState, setQaModalState] = useState<{
    filename: string;
    resolve: (mode: CtsQaColumnMode | undefined) => void;
  } | null>(null);

  useEffect(() => {
    compiledCtsFilesRef.current = {};
  }, [projectChips, projectId]);

  const ensureCtsFiles = useCallback(
    async (includeQaRuleInfo: boolean): Promise<Dictionary<string>> => {
      const cacheKey = includeQaRuleInfo ? 'withQaRuleInfo' : 'plain';
      const cached = compiledCtsFilesRef.current[cacheKey];
      if (cached) {
        return cached;
      }
      const { ctsBySampleId } = await fetchProjectCts(queryClient, projectId);
      const compiled = compileCtsFiles(projectChips, flattenProjectCtsRows(ctsBySampleId), { includeQaRuleInfo });
      compiledCtsFilesRef.current[cacheKey] = compiled;
      return compiled;
    },
    [queryClient, projectId, projectChips],
  );

  // The modal only resolves the admin's choice; the rows are loaded and compiled after it has closed
  const finishQaModal = useCallback(
    (mode: CtsQaColumnMode | undefined) => {
      if (qaModalState) {
        qaModalState.resolve(mode);
        setQaModalState(null);
      }
    },
    [qaModalState],
  );

  const handleQaModalClose = useCallback(() => {
    if (!qaModalState) return;
    finishQaModal(undefined);
  }, [qaModalState, finishQaModal]);

  const getCtFileContent = useCallback(
    async (filename: string) => {
      if (!isResistomapAdmin) {
        return (await ensureCtsFiles(false))[filename];
      }
      const mode = await new Promise<CtsQaColumnMode | undefined>(resolve => {
        setQaModalState({ filename, resolve });
      });
      if (!mode) {
        return undefined;
      }
      return (await ensureCtsFiles(mode === 'include'))[filename];
    },
    [isResistomapAdmin, ensureCtsFiles],
  );

  return (
    <>
      <GridView>
        <RSecondTitle>Downloads and Links</RSecondTitle>
        <Grid>
          {!isCtOnly && (
            <GridCell widthpercentage={100}>
              <RThirdTitle>Platform Documentation</RThirdTitle>
              <UrlLink href="https://eu1.hubs.ly/H0v11gT0" text="Details view usage instructions" />
              {!hideResearchOnlyDemoExtras && <UrlLink href="/assay-log" text="Assay quality assurance change log" />}
            </GridCell>
          )}
          {projectReferences.length > 0 && (
            <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
              <RThirdTitle>References</RThirdTitle>
              {projectReferences.map(reference => (
                <UrlLink key={reference.href} href={reference.href} text={reference.text} />
              ))}
            </GridCell>
          )}
          {showAnalysisResultsSection && (
            <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
              <RThirdTitle>Analysis Results</RThirdTitle>
              {downloadableNormalisationModes.map(mode => (
                <HeatmapDownloadRow
                  key={mode}
                  mode={mode}
                  isActive={mode === normalisationMode}
                  samplesByUID={project.samplesByUID}
                  getAssayInfo={getAssayInfo}
                />
              ))}
              {detectedGenesDownloadNormalisationMode && (
                <DownloadLink
                  filename="Numbers of detected genes.csv"
                  analyticsMetadata={{
                    downloadKind: 'result',
                    filename: 'Numbers of detected genes.csv',
                    label: 'Numbers of detected genes',
                  }}
                  getFileContent={() =>
                    getNumberOfDetectedGenesByBioNumCsv(
                      project.samplesByUID,
                      getGroup,
                      detectedGenesDownloadNormalisationMode,
                    )
                  }
                />
              )}
              {showLegacyProjectFiles &&
                plotFileNames.map((filename, key) => (
                  <DownloadLink
                    key={`dplot-${key}`}
                    filename={filename}
                    projectId={project.id}
                    analyticsMetadata={{
                      downloadKind: 'result',
                      filename,
                      label: 'Legacy result export',
                    }}
                  />
                ))}
            </GridCell>
          )}
          {user && hasCtFiles && !hideResearchOnlyDemoExtras && (
            <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
              <RThirdTitle>qPCR Raw Data</RThirdTitle>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                {chunk(ctsFilenames, 4).map((fileChunk, chunkKey) => (
                  <div key={`txtchunk${chunkKey}`} style={{ flexDirection: 'column' }}>
                    {fileChunk.map((filename, key) => {
                      const chipId = project.chips.find(chip => `${chip.name}.txt` === filename)?.id;
                      const machineLabel = machineLabelByFilename[filename];
                      return (
                        <Box key={key} display="flex" alignItems="center">
                          <DownloadLink
                            filename={filename}
                            getFileContent={async () => await getCtFileContent(filename)}
                          />
                          {machineLabel && (
                            <Box as="span" marginLeft={1} color="gray.500">
                              {machineLabel}
                            </Box>
                          )}
                          {isModifyAsAdminActive && chipId && (
                            <span onClick={() => window.open(`/chip/${chipId}`, '_blank')}>
                              <ExternalLinkIcon style={{ cursor: 'pointer' }} />
                            </span>
                          )}
                        </Box>
                      );
                    })}
                  </div>
                ))}
              </div>
            </GridCell>
          )}
          {workbookLinks.length > 0 && (
            <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
              <RThirdTitle>Lab workbooks</RThirdTitle>
              {workbookLinks.map(link => (
                <UrlLink key={link.id} text={getWorkbookLabel(link)} href={`/lab/${link.id}`} />
              ))}
            </GridCell>
          )}

          {!isCtOnly &&
            (isModifyAsAdminActive ? (
              <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
                <RThirdTitle>Internal</RThirdTitle>
                {project.customerSheetLink && (
                  <UrlLink text="Samples and primers sheet" href={project.customerSheetLink} />
                )}
                {uniq(project.chips.map(chip => chip.labSheetUrl)).map(
                  (labSheetUrl, index) =>
                    labSheetUrl && <UrlLink key={`lab-sheet-${index}`} text={`Lab sheet`} href={labSheetUrl} />,
                )}
              </GridCell>
            ) : (
              isNonDemo(user) &&
              !hideResearchOnlyDemoExtras &&
              !hasOverviewForProject(user, project.id) &&
              project.customerSheetLink && (
                <GridCell style={{ paddingTop: theme.oldSpacing.m }} widthpercentage={100}>
                  <RThirdTitle>Project specification</RThirdTitle>
                  <UrlLink text="Samples and primers sheet" href={project.customerSheetLink} />
                </GridCell>
              )
            ))}
        </Grid>
      </GridView>
      {!isCtOnly && (
        <MultiOptionConfirmModal
          isOpen={qaModalState !== null}
          onClose={handleQaModalClose}
          title="Include QA metadata?"
          message="For internal use only. Including QA rule information adds admin-only rejection metadata to each technical replicate in the exported file."
          options={[
            {
              key: 'include-qa',
              label: 'Include QA column',
              description: 'Adds the rule id and description explaining why each technical replicate was disqualified.',
              onSelect: () => {
                finishQaModal('include');
              },
              colorScheme: 'blue',
              variant: 'solid',
            },
            {
              key: 'skip-qa',
              label: 'Skip QA column',
              description:
                'Download the standard raw Cq file without internal QA metadata. The Takara-compatible Ct column name is preserved.',
              onSelect: () => {
                finishQaModal('skip');
              },
              variant: 'outline',
            },
          ]}
          cancelLabel="Cancel download"
        />
      )}
    </>
  );
}

function getWorkbookLabel(link: LabWorkbookLink): string {
  const base = link.name?.trim() ?? link.sampleSheetName?.trim() ?? `Workbook ${link.id.slice(0, 8).toUpperCase()}`;
  return `${base} (${link.organisationName})`;
}

export function getExtraFilenames(project: Project) {
  return project.filenames?.filter(name => name.endsWith('.pdf') || name.endsWith('.csv')) || [];
}

interface HeatmapDownloadRowProps {
  mode: NormalisationMode;
  isActive: boolean;
  samplesByUID: FullSamplesByUID;
  getAssayInfo: (assay: string) => AssayInfo | undefined;
}

function HeatmapDownloadRow({ mode, isActive, samplesByUID, getAssayInfo }: HeatmapDownloadRowProps) {
  const filename = `${NormalisedValueName[mode]}.csv`;
  return (
    <Box display="flex" alignItems="center">
      <DownloadLink
        filename={filename}
        analyticsMetadata={{
          downloadKind: 'result',
          filename,
          label: NormalisedValueName[mode],
        }}
        getFileContent={() => getHeatmapCsvExportContent(samplesByUID, mode, getAssayInfo)}
      />
      {isActive && (
        <Box as="span" marginLeft={2} color="gray.500" fontSize="xs" fontStyle="italic">
          · current view
        </Box>
      )}
    </Box>
  );
}
